/**
 * Analytics Appels — Pilotage des appels sortants.
 *
 * Ce fichier est l'orchestrateur mince : il gère le state global (onglet
 * actif, filtres partagés, options de dropdown), assemble la barre de
 * filtres + la barre d'onglets, et délègue le rendu de chaque onglet aux
 * composants extraits dans `./analytics/*`.
 *
 * Iso-comportement (refactor 2026-07-22) — aucun changement fonctionnel
 * par rapport à la version monolithique précédente. Si tu changes quelque
 * chose ici, re-run la régression backend (56 tests pytest) + testing
 * agent frontend.
 */
import { motion } from "framer-motion";
import { LineChart as LineChartIcon } from "lucide-react";
import { useEffect, useState } from "react";

import { api } from "../lib/api";
import { ComparatifsTab } from "./analytics/ComparatifsTab";
import { DetailTab } from "./analytics/DetailTab";
import { FunnelTab } from "./analytics/FunnelTab";
import { OverviewTab } from "./analytics/OverviewTab";
import { ReachabilityTab } from "./analytics/ReachabilityTab";
import { defaultPeriod, FiltersBar, TABS } from "./analytics/shared";

export default function AnalyticsCalls() {
    const [tab, setTab] = useState("overview");
    const [filters, setFilters] = useState(() => ({
        ...defaultPeriod(), offer_id: null, recruiter_id: null, source: null,
    }));
    const [offers, setOffers] = useState([]);
    const [recruiters, setRecruiters] = useState([]);
    const [sources, setSources] = useState([]);

    useEffect(() => {
        // Options for filter dropdowns
        api.get("/layan/offers").then(({ data }) => {
            setOffers((data?.items || data || []).map((o) => ({
                id: o.id || o.layan_id, title: o.title || o.name || "—",
            })));
        }).catch(() => {});
        api.get("/users").then(({ data }) => {
            const arr = data?.items || data || [];
            setRecruiters(
                arr.filter((u) => u.role === "pre_recruteur" || (u.additional_roles || []).includes("pre_recruteur"))
                    .map((u) => ({ id: u.id, name: u.name }))
            );
        }).catch(() => {});
        api.get("/candidates/source-counts").then(({ data }) => {
            const s = data?.sources || data?.by_source || [];
            setSources(s.map((x) => x.source || x.key).filter(Boolean));
        }).catch(() => {});
    }, []);

    return (
        <div className="min-h-full bg-shariti-cream" data-testid="analytics-calls-page">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 space-y-5">
                {/* Header */}
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-shariti-black text-shariti-orange text-[10px] font-black uppercase tracking-[0.2em]">
                            <LineChartIcon className="w-3 h-3" />
                            Analytics Appels
                        </div>
                        <h1 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight text-shariti-black">
                            Pilotage des appels sortants
                        </h1>
                        <p className="text-sm text-slate-500 mt-1 max-w-2xl">
                            Volume, joignabilité, funnel de qualification et recommandations IA — pour appeler au bon moment, au bon rythme, sur les bons candidats.
                        </p>
                    </div>
                </div>

                {/* Filters */}
                <FiltersBar
                    filters={filters}
                    onChange={setFilters}
                    offers={offers}
                    recruiters={recruiters}
                    sources={sources}
                />

                {/* Tabs */}
                <div className="flex items-center gap-1 border-b border-slate-200 overflow-x-auto" data-testid="analytics-tabs">
                    {TABS.map((t) => {
                        const isActive = tab === t.key;
                        return (
                            <button
                                key={t.key}
                                type="button"
                                onClick={() => t.enabled ? setTab(t.key) : null}
                                data-testid={`analytics-tab-${t.key}`}
                                className={`inline-flex items-center gap-2 px-4 py-2.5 text-xs font-black uppercase tracking-wider transition border-b-2 -mb-px whitespace-nowrap ${
                                    isActive
                                        ? "border-shariti-orange text-shariti-orange"
                                        : t.enabled
                                            ? "border-transparent text-slate-500 hover:text-shariti-black"
                                            : "border-transparent text-slate-300 cursor-not-allowed"
                                }`}
                            >
                                <t.icon className="w-3.5 h-3.5" />
                                {t.label}
                                {!t.enabled && (
                                    <span className="ml-1 text-[8px] uppercase tracking-widest bg-slate-100 text-slate-400 px-1.5 py-0.5 rounded-full">
                                        Bientôt
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* Tab content */}
                <motion.div key={tab} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}>
                    {tab === "overview" && <OverviewTab filters={filters} />}
                    {tab === "reachability" && <ReachabilityTab filters={filters} />}
                    {tab === "funnel" && <FunnelTab filters={filters} />}
                    {tab === "compare" && <ComparatifsTab filters={filters} />}
                    {tab === "detail" && <DetailTab filters={filters} />}
                </motion.div>
            </div>
        </div>
    );
}
